import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { GraduationCap, AlertCircle, Ban, Building2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [suspensionInfo, setSuspensionInfo] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuspensionInfo(null);
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Connexion réussie!');
      navigate('/dashboard');
    } catch (err) {
      // Keep email for retry; clear password for security
      setPassword('');

      const message = err.response?.data?.detail || 'Erreur de connexion';
      
      // Check if it's a center suspension error
      if (message.startsWith('CENTRE_SUSPENDU|')) {
        const parts = message.split('|');
        const reason = parts[1] || 'Raison non spécifiée';
        const centerName = parts[2] || 'Votre centre';
        setSuspensionInfo({ reason, centerName });
      } else {
        setError(message);
        // Keep the error visible long enough to read (default auto-dismiss is too quick)
        toast.error(message, { duration: 8000 });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <img 
              src="/images/ofpilot-logo-login.png" 
              alt="OFPilot" 
              className="h-20 w-auto object-contain mb-8"
            />
            <h1 className="text-4xl font-bold text-slate-900 dark:text-white">Bienvenue 👋</h1>
            <p className="mt-2 text-slate-500 dark:text-slate-400">Connectez-vous pour continuer</p>
          </div>

          <Card data-testid="login-card">
            <CardContent className="pt-6">
              {/* Suspension Block */}
              {suspensionInfo && (
                <div className="mb-6 p-5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl" data-testid="suspension-block">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center flex-shrink-0">
                      <Ban className="w-6 h-6 text-red-600 dark:text-red-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-red-700 dark:text-red-400 text-lg">
                        Accès suspendu
                      </h3>
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-300">
                          <Building2 className="w-4 h-4" />
                          <span className="font-medium">{suspensionInfo.centerName}</span>
                        </div>
                        <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
                          <p className="text-sm font-medium text-red-800 dark:text-red-300">
                            Motif : {suspensionInfo.reason}
                          </p>
                        </div>
                        <p className="text-sm text-red-600 dark:text-red-400 mt-3">
                          L'accès à la plateforme a été temporairement suspendu pour l'ensemble des utilisateurs de ce centre (administrateurs, formateurs et étudiants).
                        </p>
                        <p className="text-sm text-red-600 dark:text-red-400">
                          Veuillez contacter l'administration de la plateforme pour régulariser votre situation.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 p-3 rounded-lg flex items-center gap-2" data-testid="login-error">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre@email.fr"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="email-input"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Mot de passe</Label>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="pr-10"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                      data-testid="toggle-password-btn"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Forgot Password Link */}
                <div className="text-right">
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300 hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Mot de passe oublié ?
                  </Link>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-violet-600 hover:bg-violet-700"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Connexion...' : 'Se connecter'}
                </Button>

                <p className="text-sm text-slate-500 dark:text-slate-400 text-center pt-2">
                  La création de compte se fait uniquement sur invitation. Contactez votre administrateur pour obtenir vos identifiants.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right Side - Illustration */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-[#EBE4FE] p-6 overflow-hidden">
        <img
          src="/images/bgapp.png"
          alt="OFPilot — Apprendre. Former. Piloter. Tout votre écosystème formation dans une seule plateforme."
          className="h-full w-full object-contain"
        />
      </div>
    </div>
  );
};

export default LoginPage;
